import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useLocalizedStrings } from "../../../Localization";
import { BreadcrumbProps, Button, Icon } from "@aidkitorg/component-library";
import { AdminPanelPage } from "../../AdminPanelPage";
import { usePost } from "../../../API";
import { ApplicantDataTable } from "./ApplicantDataTable";
import { AppliedFiltersDisplay } from "./AppliedFiltersDisplay";
import { ApplicantGroupFilterConfig } from "@aidkitorg/types/lib/admin-panel/filters";
import { BooleanExprToFilterGroupConverter } from "../../shared/utils/filterConverters";
import { BooleanExpr } from "@aidkitorg/types/lib/survey";
import { useSettings } from "../../../Contexts/SettingsContext";

interface ApplicantGroupMetadata {
  id: string;
  name: string;
  description: string;
  filters?: ApplicantGroupFilterConfig;
}

export const ApplicantGroupDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const L = useLocalizedStrings();
  const strings = L.admin_panel.settings.applicant_groups;
  const [groupMetadata, setGroupMetadata] = useState<ApplicantGroupMetadata | null>(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [error, setError] = useState<boolean>(false);

  const { fields } = useSettings();
  const fetchApplicantGroup = usePost("/api/admin_panel/applicant_group");

  // Create field labels map for display
  const fieldLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    fields.forEach(field => {
      labels[field.id] = field.displayName || field.id;
    });
    return labels;
  }, [fields]);

  // Fetch group metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setIsLoadingMetadata(true);
        setError(false);
        const response = await fetchApplicantGroup({ id });

        if (response?.success && response.data) {
          // Convert filters from BooleanExpr to ApplicantGroupFilterConfig if needed
          let filters = response.data.filters;

          // Check if filters.config is a BooleanExpr (has 'kind' property) instead of FilterGroup
          if (filters && filters.config && 'kind' in filters.config) {
            // Convert BooleanExpr to FilterGroup format
            const filterGroup = BooleanExprToFilterGroupConverter.convert(filters.config as BooleanExpr);
            if (filterGroup) {
              filters = { enabled: true, config: filterGroup };
            }
          }

          setGroupMetadata({
            ...response.data,
            filters
          });
        } else {
          throw new Error("Failed to fetch applicant group details");
        }
      } catch (err) {
        console.error("Error fetching applicant group:", err);
        setError(true);
      } finally {
        setIsLoadingMetadata(false);
      }
    };

    if (id) {
      fetchMetadata();
    }
  }, [id]);

  const breadcrumbs: BreadcrumbProps["items"] = useMemo(
    () => [
      {
        label: L.admin_panel.nav.applicants,
      },
      {
        label: L.admin_panel.nav.applicant_groups,
        href: "/admin-panel/applicants/applicant-groups",
      },
      {
        label: groupMetadata?.name || "...",
      },
    ],
    [L, groupMetadata]
  );

  const handleBack = () => {
    history.push("/admin-panel/applicants/applicant-groups");
  };

  const handleEdit = () => {
    history.push(`/admin-panel/applicants/applicant-groups/${id}/edit`);
  };

  const pageActionButtons = [
    <Button
      key="back"
      variant="secondary"
      onClick={handleBack}
      icon={<Icon name="ArrowLeftIcon" type="solid" role="decorative" />}
    >
      {strings.back_to_groups}
    </Button>,
    <Button
      key="edit"
      variant="primary"
      onClick={handleEdit}
      icon={<Icon name="PencilIcon" type="solid" role="decorative" />}
    >
      {strings.edit}
    </Button>,
  ];

  return (
    <AdminPanelPage
      title={groupMetadata?.name || "..."}
      description={groupMetadata?.description}
      breadcrumbs={breadcrumbs}
      pageActionButtons={pageActionButtons}
    >
      <div className="flex flex-col w-100 gap-4">
        {groupMetadata?.filters && <AppliedFiltersDisplay filters={groupMetadata.filters} fieldLabels={fieldLabels} />}
        <ApplicantDataTable applicantGroupId={id} isLoading={isLoadingMetadata} error={error} />
      </div>
    </AdminPanelPage>
  );
};

export default ApplicantGroupDetailPage;
